import { ref, type ComputedRef, type Ref } from 'vue'

import type { ApprovalStage, ThoughtTraceItem, WorkflowInstance } from '@/types/workflow'
import { consumeGenerationAction, type WorkflowGenerationAction } from '@/utils/workflowGeneration'

interface UseWorkspaceExecutionOptions {
  projectId: number
  requestedStage: ComputedRef<ApprovalStage | null>
  currentStage: ComputedRef<ApprovalStage>
  canApproveCurrentStage: ComputedRef<boolean>
  storyboardApprovalHint: ComputedRef<string>
  revisionTargetStage: ComputedRef<ApprovalStage>
  acting: Ref<boolean>
  activeAction: Ref<WorkflowGenerationAction | null>
  workflow: Ref<WorkflowInstance | null>
  reviewFeedback: Ref<string>
  editorContent: Ref<string>
  ensureStoryboardScenesReady: () => Promise<boolean>
  handleSaveFinalWorkspace: () => Promise<unknown>
  syncEditorFromWorkflow: (force: boolean) => void
  loadBundle: (showError?: boolean) => Promise<void>
  syncRouteToWorkflowStage: (preferredStage?: ApprovalStage | null) => Promise<void>
  scrollWorkspaceToTop: () => void
  startPolling: () => void
  stopPolling: () => void
  startWorkflowAction: (request: NonNullable<WorkflowGenerationAction['startRequest']>) => Promise<unknown>
  approveStageAction: (stage: ApprovalStage, payload: { feedback: string; edited_content?: string; text_model_id?: string }) => Promise<unknown>
  requestRevisionAction: (
    stage: ApprovalStage,
    payload: { feedback: string; revision_notes: string; edited_content?: string; return_to_stage?: ApprovalStage; text_model_id?: string }
  ) => Promise<WorkflowInstance>
  resumeWorkflowAction: () => Promise<unknown>
  routerPush: (path: string) => Promise<unknown>
  success: (message: string) => void
  error: (message: string) => void
  getActionErrorMessage: (error: any) => string | null
}

export function useWorkspaceExecution(options: UseWorkspaceExecutionOptions) {
  const optimisticTrace = ref<ThoughtTraceItem[]>([])
  const liveNarrationText = ref('')
  const stageOrder: ApprovalStage[] = ['proposal', 'script', 'storyboard', 'final']
  const QUEUED_ACTION_MAX_AGE_MS = 5 * 60 * 1000

  let narrationTimer: number | null = null

  function getRevisionExecutionStage(action: WorkflowGenerationAction): ApprovalStage | undefined {
    return action.revisionTargetStage || action.stage
  }

  function isViewingUpstreamStage(pendingStage: ApprovalStage, viewedStage: ApprovalStage) {
    return stageOrder.indexOf(viewedStage) >= 0 && stageOrder.indexOf(viewedStage) < stageOrder.indexOf(pendingStage)
  }

  function isWorkflowExecutionActive(state: unknown) {
    const normalized = String(state || '').trim()
    return normalized === 'confirmed' || normalized === 'in_progress' || normalized === 'revision_in_progress'
  }

  function isWorkflowInstanceLike(value: unknown): value is WorkflowInstance {
    return Boolean(value && typeof value === 'object' && 'state' in (value as Record<string, unknown>))
  }

  function isQueuedActionStillApplicable(action: WorkflowGenerationAction) {
    if (!action.createdAt || Date.now() - action.createdAt > QUEUED_ACTION_MAX_AGE_MS) {
      return false
    }

    const workflow = options.workflow.value
    if (!workflow) {
      return true
    }

    const state = String(workflow.state || '').trim()
    const pendingStage = workflow.pending_approval?.stage as ApprovalStage | undefined
    const hasHistory = Boolean((workflow.logs || []).length)
      || Boolean((workflow.data?.completed_stages || []).length)
      || Boolean(workflow.data?.proposal_generation_result)
      || Boolean(workflow.data?.script_generation_result)
      || Boolean(workflow.data?.storyboard_generation_result)
      || Boolean(workflow.data?.final_packaging_result)

    if (action.intent === 'start') {
      return state === 'draft' && !hasHistory
    }

    if (action.intent === 'resume') {
      return state === 'on_hold' || state === 'failed'
    }

    if (action.intent === 'approve' || action.intent === 'revise') {
      return Boolean(pendingStage && action.stage && pendingStage === action.stage)
    }

    return true
  }

  function buildOptimisticTrace(action: WorkflowGenerationAction) {
    const revisionStage = getRevisionExecutionStage(action)

    if (action.intent === 'start') {
      if (action.stage === 'script') {
        optimisticTrace.value = [
          {
            id: 'script-import',
            stage: 'script_generation',
            label: '剧本载入',
            status: 'active',
            summary: '系统正在挂载导入剧本，并把项目直接切到剧本审批节点。',
            details: ['跳过需求分析与方案确认', '载入现有剧本文本并等待人工审批'],
          },
        ]
        return
      }

      optimisticTrace.value = [
        {
          id: 'demand-analysis',
          stage: 'demand_analysis',
          label: '需求理解',
          status: 'active',
          summary: 'AI 正在读取客户需求并提炼主题、受众、场景与冲突。',
          details: ['优先核对目标用户、视觉风格、参考素材和补充说明', '生成结构化需求分析结果'],
        },
        {
          id: 'proposal-generation',
          stage: 'proposal_generation',
          label: '方案草拟',
          status: 'pending',
          summary: 'AI 将把需求拆解转成可确认的方案正文。',
          details: ['输出市场洞察、用户画像、定位和创意方向'],
        },
      ]
      return
    }

    if (action.intent === 'approve' && action.stage === 'proposal') {
      optimisticTrace.value = [{
        id: 'script-generation',
        stage: 'script_generation',
        label: '剧本生成',
        status: 'active',
        summary: 'AI 正在根据确认后的方案生成剧本首稿。',
        details: ['吸收已确认方案正文', '结合客户目标用户和视觉风格输出完整剧本'],
      }]
      return
    }

    if (action.intent === 'approve' && action.stage === 'script') {
      optimisticTrace.value = [{
        id: 'storyboard-generation',
        stage: 'storyboard_generation',
        label: '分镜拆解',
        status: 'active',
        summary: 'AI 正在将剧本拆成可执行分镜。',
        details: ['同步目标用户、视觉风格和补充说明', '生成场景、动作、画面提示'],
      }]
      return
    }

    if (action.intent === 'revise' && revisionStage === 'proposal') {
      optimisticTrace.value = [{
        id: 'proposal-revision',
        stage: 'proposal_generation',
        label: '方案重做',
        status: 'active',
        summary: 'AI 正在回到方案阶段，吸收你的修改意见并重写方案正文。',
        details: ['保留需求分析结果', '优先吸收审批框中的回退原因和修改要求'],
      }]
      return
    }

    if (action.intent === 'revise' && revisionStage === 'script') {
      optimisticTrace.value = [{
        id: 'script-revision',
        stage: 'script_generation',
        label: '剧本重做',
        status: 'active',
        summary: 'AI 正在回到剧本阶段，根据你的反馈和编辑稿修订剧本。',
        details: ['先读取当前编辑后的剧本文本', '逐条吸收回退意见后重新生成'],
      }]
      return
    }

    if (action.intent === 'revise' && revisionStage === 'storyboard') {
      optimisticTrace.value = [{
        id: 'storyboard-revision',
        stage: 'storyboard_generation',
        label: '分镜重做',
        status: 'active',
        summary: 'AI 正在回到分镜阶段，按你的修改意见重做分镜拆解。',
        details: ['结合当前剧本与人工反馈重排场景', '重新生成画面提示、人物提示和节奏信息'],
      }]
      return
    }

    if (action.intent === 'approve' && action.stage === 'storyboard') {
      optimisticTrace.value = [{
        id: 'final-generation',
        stage: 'final_packaging',
        label: '成品制作',
        status: 'active',
        summary: '系统正在尝试进入视频与交付阶段，但只有生成出真实成片后才会进入最终预览。',
        details: ['如果未配置视频能力或尚无可播放成片，会明确报错并停留在当前工作台'],
      }]
      return
    }

    if (action.intent === 'resume') {
      optimisticTrace.value = [{
        id: 'resume-workflow',
        stage: 'resume',
        label: '恢复执行',
        status: 'active',
        summary: 'AI 正在从中断点恢复工作流。',
        details: ['重新进入当前未完成阶段', '同步新的执行结果'],
      }]
      return
    }

    optimisticTrace.value = []
  }

  function buildActionNarration(action: WorkflowGenerationAction): string {
    const revisionStage = getRevisionExecutionStage(action)

    if (action.intent === 'start') {
      if (action.stage === 'script') {
        return ['已接收导入剧本。', '正在跳过需求分析与方案确认。', '接下来会直接进入剧本审批工作台。'].join('\n')
      }
      return ['已接收项目输入。', '正在整理需求重点、目标用户和表达方向。', '接下来会生成第一版可确认结果。'].join('\n')
    }

    if (action.intent === 'approve' && action.stage === 'proposal') {
      return ['已锁定你确认后的方案正文。', '正在生成剧本初稿。', '完成后会自动切到剧本阶段。'].join('\n')
    }

    if (action.intent === 'approve' && action.stage === 'script') {
      return ['已锁定你确认后的剧本文本。', '正在拆分场景、镜头和画面说明。', '分镜生成完成后，会回到当前工作台供你继续确认。'].join('\n')
    }

    if (action.intent === 'approve' && action.stage === 'storyboard') {
      return ['已锁定分镜结果，系统开始尝试推进视频与交付阶段。', '只有在生成出真实可播放视频后，这里才会进入成片预览。', '如果未配置视频能力，系统会直接返回明确错误，不再假装成功。'].join('\n')
    }

    if (action.intent === 'revise') {
      if (revisionStage === 'proposal') {
        return ['已收到你的回退意见。', '系统将返回方案阶段，优先吸收审批框中的修改要求。', '新方案生成完成后，会重新停在方案审批节点。'].join('\n')
      }
      if (revisionStage === 'script') {
        return ['已收到你的回退意见。', '系统将返回剧本阶段，优先结合当前编辑稿和审批意见重写剧本。', '新剧本生成完成后，会重新停在剧本审批节点。'].join('\n')
      }
      if (revisionStage === 'storyboard') {
        return ['已收到你的回退意见。', '系统将返回分镜阶段，优先根据审批意见重做镜头拆解。', '新分镜生成完成后，会重新停在分镜审批节点。'].join('\n')
      }
      return ['已收到你的退回意见。', '系统会优先根据你的修改说明和当前编辑版重做内容。', '新版本生成完成后，会停回当前审批节点让你再次确认。'].join('\n')
    }

    if (action.intent === 'resume') {
      return ['正在恢复上一次中断的流程。', '系统会从未完成的阶段继续处理，并自动刷新结果。'].join('\n')
    }

    return ''
  }

  function stopLiveNarration() {
    if (narrationTimer !== null) {
      window.clearInterval(narrationTimer)
      narrationTimer = null
    }
  }

  function startLiveNarration(action: WorkflowGenerationAction) {
    stopLiveNarration()
    const source = buildActionNarration(action)
    liveNarrationText.value = ''
    if (!source) {
      return
    }

    let index = 0
    narrationTimer = window.setInterval(() => {
      index += 1
      liveNarrationText.value = source.slice(0, index)
      if (index >= source.length) {
        stopLiveNarration()
      }
    }, 24)
  }

  async function executeQueuedAction(action: WorkflowGenerationAction) {
    if (!options.acting.value || options.activeAction.value !== action) {
      options.activeAction.value = action
      options.acting.value = true
    }
    options.scrollWorkspaceToTop()
    buildOptimisticTrace(action)
    startLiveNarration(action)
    options.startPolling()

    try {
      let actionResponse: unknown = null

      if (action.intent === 'start' && action.startRequest) {
        actionResponse = await options.startWorkflowAction(action.startRequest)
        options.success(action.stage === 'script' ? '剧本已导入，正在进入剧本审批' : 'AI 已开始需求分析')
      } else if (action.intent === 'approve' && action.stage) {
        actionResponse = await options.approveStageAction(action.stage, {
          feedback: action.feedback || '确认通过',
          edited_content: action.editedContent,
          text_model_id: action.textModelId,
        })
        options.success('已确认并继续生成')
      } else if (action.intent === 'revise' && action.stage) {
        actionResponse = await options.requestRevisionAction(action.stage, {
          feedback: action.feedback || '请根据修改意见重新生成',
          revision_notes: action.feedback || '请根据修改意见重新生成',
          edited_content: action.editedContent,
          return_to_stage: action.revisionTargetStage,
          text_model_id: action.textModelId,
        })
        options.success('修改请求已提交，AI 正在重新生成')
      } else if (action.intent === 'resume') {
        actionResponse = await options.resumeWorkflowAction()
        options.success('工作流已恢复')
      }

      if (isWorkflowInstanceLike(actionResponse)) {
        options.workflow.value = actionResponse
      }

      await options.loadBundle(false)
      await options.syncRouteToWorkflowStage(action.intent === 'revise' ? getRevisionExecutionStage(action) || null : null)
      options.reviewFeedback.value = ''
      options.syncEditorFromWorkflow(true)
      options.scrollWorkspaceToTop()
    } catch (error: any) {
      const message = options.getActionErrorMessage(error)
      if (message) {
        options.error(message)
      }
      await options.loadBundle(false)
      await options.syncRouteToWorkflowStage(action.intent === 'revise' ? getRevisionExecutionStage(action) || null : null)
    } finally {
      if (!isWorkflowExecutionActive(options.workflow.value?.state)) {
        options.stopPolling()
      }
      stopLiveNarration()
      optimisticTrace.value = []
      liveNarrationText.value = ''
      options.acting.value = false
      options.activeAction.value = null
    }
  }

  async function handleApproveFromWorkspace(payload?: { textModelId?: string }) {
    if (!options.canApproveCurrentStage.value) {
      if (options.currentStage.value === 'storyboard') {
        options.error(options.storyboardApprovalHint.value)
      }
      return
    }

    if (options.currentStage.value === 'storyboard') {
      const ready = await options.ensureStoryboardScenesReady()
      if (!ready) {
        return
      }
    }

    if (options.currentStage.value === 'final') {
      await options.handleSaveFinalWorkspace()
    }

    if (!options.workflow.value?.pending_approval) {
      return
    }

    const pendingStage = options.workflow.value.pending_approval.stage as ApprovalStage
    const stage = isViewingUpstreamStage(pendingStage, options.currentStage.value)
      ? options.currentStage.value
      : pendingStage
    const action: WorkflowGenerationAction = {
      intent: 'approve',
      stage,
      feedback: options.reviewFeedback.value || '确认通过',
      editedContent: options.editorContent.value.trim() || undefined,
      textModelId: payload?.textModelId,
      createdAt: Date.now(),
    }

    await executeQueuedAction(action)
  }

  async function handleRequestRevision(payload?: { textModelId?: string }) {
    if (!options.workflow.value?.pending_approval) {
      return
    }

    const pendingStage = options.workflow.value.pending_approval.stage as ApprovalStage
    const viewingUpstreamStage = isViewingUpstreamStage(pendingStage, options.currentStage.value)
    const revisionTargetStage = options.revisionTargetStage.value

    if (
      options.currentStage.value === revisionTargetStage
      || viewingUpstreamStage
    ) {
      await executeQueuedAction({
        intent: 'revise',
        stage: viewingUpstreamStage ? options.currentStage.value : pendingStage,
        revisionTargetStage: viewingUpstreamStage ? options.currentStage.value : revisionTargetStage,
        feedback: options.reviewFeedback.value.trim() || '请根据修改意见重新生成',
        editedContent: options.editorContent.value.trim() || undefined,
        textModelId: payload?.textModelId,
        createdAt: Date.now(),
      })
      return
    }

    await options.syncRouteToWorkflowStage(revisionTargetStage)
    options.syncEditorFromWorkflow(true)
    options.scrollWorkspaceToTop()
    options.success('已切回上一阶段，请修改后重新确认')
  }

  async function handleResumeFromWorkspace() {
    await executeQueuedAction({
      intent: 'resume',
      stage: options.requestedStage.value || undefined,
      createdAt: Date.now(),
    })
  }

  async function consumeQueuedWorkspaceAction() {
    if (options.acting.value) {
      return false
    }

    const queuedAction = consumeGenerationAction(options.projectId)
    if (!queuedAction) {
      return false
    }

    if (!isQueuedActionStillApplicable(queuedAction)) {
      return false
    }

    await executeQueuedAction(queuedAction)
    return true
  }

  return {
    consumeQueuedWorkspaceAction,
    executeQueuedAction,
    handleApproveFromWorkspace,
    handleRequestRevision,
    handleResumeFromWorkspace,
    stopLiveNarration,
  }
}